// ===========================================
// Family Payments React Query Hooks
// ===========================================
// The admin family-details "Payment history" section (family payment history,
// 2026-09-27). md/plan/family-payment-history.md Task 4.1.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  familyPaymentsApi,
  RecordRegistrationPaymentInput,
  UpdateRegistrationPaymentInput,
} from '../api/familyPayments.api';
import { invoiceKeys } from './useInvoices';
import { apiErrorMessage } from '../utils/apiError';

// ===========================================
// QUERY KEYS
// ===========================================
// Nested UNDER invoiceKeys.all so the invoice payment mutations' existing
// `invalidateQueries({ queryKey: invoiceKeys.all })` (useRecordPayment /
// useUpdatePayment / useDeletePayment) refreshes this list for free — an
// invoice payment recorded elsewhere shows up here without a second wire-up.
export const familyPaymentKeys = {
  all: [...invoiceKeys.all, 'family-payments'] as const,
  history: (familyId: string, year: number) => [...familyPaymentKeys.all, familyId, year] as const,
};

// ===========================================
// QUERIES
// ===========================================

export function useFamilyPaymentHistory(familyId: string | undefined, year: number, enabled: boolean) {
  return useQuery({
    queryKey: familyPaymentKeys.history(familyId ?? '', year),
    queryFn: () => familyPaymentsApi.getHistory(familyId as string, year),
    enabled: !!familyId && enabled,
    // Keep last year's rows on screen while the new year loads, instead of
    // blanking the whole section (and the Year Select) on every change.
    placeholderData: keepPreviousData,
  });
}

// ===========================================
// MUTATIONS
// ===========================================

export function useRecordRegistrationPayment() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ familyId, data }: { familyId: string; data: RecordRegistrationPaymentInput }) =>
      familyPaymentsApi.recordRegistrationPayment(familyId, data),
    onSuccess: () => {
      enqueueSnackbar('Registration payment recorded', { variant: 'success' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: familyPaymentKeys.all });
    },
    // No error snackbar: RecordRegistrationPaymentDialog shows the failure
    // inline (an Alert under the fields), so a toast would duplicate it.
  });
}

export function useUpdateRegistrationPayment() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRegistrationPaymentInput }) =>
      familyPaymentsApi.updateRegistrationPayment(id, data),
    onSuccess: () => {
      enqueueSnackbar('Registration payment updated', { variant: 'success' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: familyPaymentKeys.all });
    },
    // No error snackbar: RecordRegistrationPaymentDialog (amend mode) shows
    // the failure inline, so a toast would duplicate it.
  });
}

export function useAssignRegistrationPaymentStudent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ familyId, id, studentId }: { familyId: string; id: string; studentId: string }) =>
      familyPaymentsApi.assignRegistrationPaymentStudent(familyId, id, studentId),
    onSuccess: () => {
      enqueueSnackbar('Registration fee assigned', { variant: 'success' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: familyPaymentKeys.all });
    },
    // No error snackbar: AssignRegistrationFeeDialog shows the failure inline.
  });
}

export function useDeleteRegistrationPayment() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => familyPaymentsApi.deleteRegistrationPayment(id),
    onSuccess: () => {
      enqueueSnackbar('Registration payment deleted', { variant: 'success' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: familyPaymentKeys.all });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(apiErrorMessage(error, 'Failed to delete the registration payment'), {
        variant: 'error',
      });
    },
  });
}

export function useWaiveRegistrationFee() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ familyId, reason }: { familyId: string; reason: string }) =>
      familyPaymentsApi.waiveRegistrationFee(familyId, reason),
    onSuccess: () => {
      enqueueSnackbar('Registration fee waived', { variant: 'success' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: familyPaymentKeys.all });
    },
    // No error snackbar: WaiveRegistrationFeeDialog shows the failure inline.
  });
}

export function useRevokeRegistrationFeeWaiver() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ familyId, reason }: { familyId: string; reason?: string }) =>
      familyPaymentsApi.revokeRegistrationFeeWaiver(familyId, reason),
    onSuccess: () => {
      enqueueSnackbar('Waiver revoked', { variant: 'success' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: familyPaymentKeys.all });
    },
    // No error snackbar: FamilyPaymentHistory's revoke dialog now shows the
    // failure inline (an Alert), so a toast would duplicate it.
  });
}

// #372: the family late-fee exemption. Invalidates the family history AND every invoice (the
// invoice page shows "family exempt" beside its fees).
export function useSetLateFeeExemption() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ familyId, reason }: { familyId: string; reason: string }) =>
      familyPaymentsApi.setLateFeeExemption(familyId, reason),
    onSuccess: () => enqueueSnackbar('Family exempted from late fees', { variant: 'success' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: familyPaymentKeys.all });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    // No error snackbar: the dialog shows the failure inline.
  });
}

export function useClearLateFeeExemption() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (familyId: string) => familyPaymentsApi.clearLateFeeExemption(familyId),
    onSuccess: () => enqueueSnackbar('Late-fee exemption cleared', { variant: 'success' }),
    // No dialog shows this one's failure: without a snackbar the button re-enables in silence and
    // the admin believes fees are back on.
    onError: (error: unknown) =>
      enqueueSnackbar(apiErrorMessage(error, 'Could not clear the exemption'), { variant: 'error' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: familyPaymentKeys.all });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}
